'use client'

import dynamic from 'next/dynamic'

import { cn } from '@/lib/utils'

/**
 * The horizon behind sign-in, registration and password reset.
 *
 * Owner's pick, by link: React Bits' `GradientWaves` with the horizon set to
 * `#e127ff` — *„takie coś w panelu logowania/rejestracji"*. It is mounted from
 * the `(auth)` layout rather than from each page, which is the whole reason it
 * exists as its own component:
 *
 *  - **One WebGL context for the group.** Sign-in and registration are two
 *    routes that people bounce between, and a canvas mounted per page is a
 *    context torn down and rebuilt on every switch — the most expensive thing
 *    on the screen, recreated for a tab change.
 *  - **The background does not blink.** Rendered from the layout it sits above
 *    the loading boundary, so a navigation replaces the card and leaves the
 *    water running, the same way the site header stays put.
 *
 * ## It is the ONLY WebGL on these screens, and that was a trade
 *
 * The sign-in page carried `ChromeLogo3D` (three.js, its own context, ~3 MB of
 * library) and the registration card carried `ChromeLogo` (`MetallicPaint`,
 * another one). Adding this behind either would have put two GL contexts on one
 * screen — two renderers, two frame loops, and on a browser at its context
 * limit the older one gets reclaimed, which is a wordmark that vanishes with a
 * console warning nobody sees. Both marks are the flat `FlouWordmark` now: the
 * spectacle moved from the logo to the page behind it.
 */

/**
 * `ssr: false` because there is no canvas on the server, and lazily because
 * `ogl` has no business in the first-load bundle of a page whose job is a
 * password field. The form must be typeable before the water arrives.
 *
 * No `loading` placeholder: the fallback for a background is the background,
 * i.e. the page's own brand black, which is already there.
 */
const GradientWaves = dynamic(
  () => import('@/components/atoms/gradient-waves.client').then((m) => m.GradientWaves),
  { ssr: false },
)

/**
 * The three colours, all on the brand arc (`lib/brand-tones.ts`: hue 285–352).
 *
 * Written as OKLCH rather than as the hex the owner's link carries, because
 * that is how the rest of this site states a colour and because `#e127ff` is
 * not expressible any other way — it is `oklch(0.667 0.3 321.3)`, hue 321,
 * almost exactly the brand gradient's midpoint, and at that lightness the
 * chroma sits on the edge of sRGB. The browser gamut-maps it back to the
 * owner's pixel value; a hex here would have been a second spelling of one
 * colour with no way to tell which one had drifted.
 *
 * `HORIZON` is the band of light where the water meets the sky — the colour the
 * effect is chosen for, and the only one taken from the link.
 *
 * The other two are NOT upstream's pale pink and white, and the reason is worth
 * writing down because the names mislead. The shader picks between them by
 * height (`crest` at the top of a wave, `wave` in the trough), but with this
 * geometry the surface spends almost all of its time near the top of that
 * range — so `CREST` is effectively the colour of the water and `WAVE` only
 * shows in the troughs. With upstream's values that made the near field a pale
 * pink sheet with white peaks, two feet behind a login form. `CREST` is a deep
 * magenta (hue 318, the gradient's own midpoint) and `WAVE` a near-black violet
 * (295), so the water is dark where the card sits and the light stays at the
 * back where it was asked for.
 */
const HORIZON = 'oklch(0.667 0.3 321.3)'
const WAVE = 'oklch(0.26 0.11 295)'
const CREST = 'oklch(0.5 0.2 318)'

/**
 * Both ends of the water, given to the page.
 *
 * **The top.** Above the horizon the canvas is already transparent — no ray
 * hits anything, so the shader's own fog takes alpha to zero — but "already
 * transparent" starts at the horizon, and the header sits well above it. The
 * first stops carry the page's black down to where the light begins, so the nav
 * bar has a flat ground to be legible against and the horizon arrives as an
 * event rather than as the top of the screen.
 *
 * **The bottom**, which is the one that is not cosmetic. The canvas stops at a
 * fixed height (see the box below) and the registration form is taller than
 * that, so scrolling would otherwise walk off a straight horizontal edge where
 * violet water becomes brand black in one pixel. The last stops put the page's
 * own background back over the final third, so the water runs out instead of
 * being cut off.
 */
const SCRIM =
  'linear-gradient(to bottom, var(--flou-bg) 0%, color-mix(in oklab, var(--flou-bg) 62%, transparent) 24%, transparent 48%, transparent 66%, var(--flou-bg) 97%)'

export function AuthBackdrop({
  position = 'absolute',
}: {
  /**
   * `absolute` on the auth screens; `fixed` in the panel.
   *
   * The cap below (`max-h-[118lvh]`) is measured against the ELEMENT, and the
   * panel is a column many screens tall — absolute there would paint water
   * over the first screen and a flat wall under the rest of it. Fixed, the
   * canvas is the window, which is the shape the shader was tuned for and the
   * shape it has on `/pl/login`.
   */
  position?: 'absolute' | 'fixed'
} = {}) {
  return (
    <div
      aria-hidden="true"
      // `-z-10` and `pointer-events-none`, the same contract `PageGlow` has:
      // the layout that renders this carries `isolate`, which is what keeps a
      // negative z-index from escaping to the root stacking context and sinking
      // behind the body's background.
      //
      // `max-h-[118lvh]` is the load-bearing half of the box, and the number is
      // not arbitrary. `inset-0` alone stretches the canvas over the whole auth
      // column, and the registration form is half again a viewport tall — the
      // shader puts its horizon at a fixed fraction of the canvas's HEIGHT, so
      // on that page the horizon would have been most of a screen below the
      // fold and the visitor would see a flat violet wall. The cap puts it back
      // where it was designed to be, on every page in the group.
      //
      // The extra 18% is the runway the fade above needs: at exactly `100lvh`
      // the canvas ends at the bottom of the first screen, which is where the
      // registration form is still going — scrolling walked off a straight
      // horizontal edge. A fifth of a screen of overhang is enough for the
      // scrim's last stops to do their work off the fold.
      className={cn(
        'pointer-events-none inset-0 -z-10 max-h-[118lvh] overflow-hidden',
        position === 'fixed' ? 'fixed' : 'absolute',
      )}
    >
      <GradientWaves
        horizonColor={HORIZON}
        waveColor={WAVE}
        crestColor={CREST}
        // A bit over half the demo's speed. The demo is tuned to be looked AT
        // and this one is looked past — at 0.4 the swell crosses the card in a
        // few seconds, which is motion in the corner of the eye of somebody
        // reading a label. The wave geometry itself is upstream's: amplitude,
        // swell and turbulence are what make it read as water rather than as a
        // gradient, and the two that were lowered first (1.9 / 28 / 16) only
        // flattened it into a wash.
        speed={0.22}
        amplitude={2.5}
        swell={35}
        turbulence={20}
        // Twice the demo's fog depth, and this is the single value that decides
        // what the screen looks like. `t = fogDepth / distance` picks between
        // the horizon colour and the water, and at 15 almost nothing is near
        // enough to reach the water — every pixel came back a translucent
        // magenta fog with the card floating in the middle of it. At 30 the
        // near field is the dark violet it was chosen to be, the magenta stays
        // where it belongs (the band at the back), and white type on the left
        // column has something to be white against.
        fogDepth={30}
        // Just under four fifths, so the page's own black stays the ground and
        // the water is a thing on it rather than a replacement for it.
        opacity={0.78}
        // Back up slightly, because `opacity` took the horizon glow down with
        // everything else and the glow is the part the owner picked.
        brightness={1.12}
        // Half the default. A background that swings with every pointer move is
        // a distraction two inches from the field somebody is typing a password
        // into.
        parallaxStrength={0.25}
        // Grain, and slightly more of it than the default, because the horizon
        // is a very long, very smooth gradient in one hue — exactly the shape
        // an 8-bit framebuffer bands across.
        grainIntensity={0.07}
      />
      <div className="absolute inset-0" style={{ background: SCRIM }} />
    </div>
  )
}
